import React, { useState } from "react";
import { Button, Space } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import useDomains from "hooks/domains/useDomains";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import DomainsTable from "./DomainsTable";
import DomainViewDrawer from "./DomainViewDrawer";


export default function DomainsPage() {
  const {
    domains,
    loading,
    totalElements,
    fetchAll,
    searchDomains,
    resumeOrder,
    getDomainById,
  } = useDomains();

  const [filters, setFilters] = useState({
    domainName: "",
    dnsStatus: null,
    hostingStatus: null,
    primaryDomain: null,
  });

  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewingDomain, setViewingDomain] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // whether any server-side filter is active
  const hasActiveFilters = () =>
    filters.domainName ||
    filters.dnsStatus ||
    filters.hostingStatus ||
    (filters.primaryDomain !== null && filters.primaryDomain !== undefined);

  const runQuery = (page = 0, size = pagination.pageSize) => {
    if (hasActiveFilters()) {
      searchDomains({ ...filters, page, size });
    } else {
      fetchAll(page, size);
    }
  };

  const handleView = (record) => {
    setViewingDomain(record);
    setViewDrawerOpen(true);
  };

  const handlePageChange = (pag) => {
    setPagination(pag);
    runQuery(pag.current - 1, pag.pageSize);
  };

  const handleFiltersChange = (updater) => {
    setFilters(updater);
    // reset to first page whenever filters change
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRefresh = () => {
    runQuery(pagination.current - 1, pagination.pageSize);
  };

  const handleResume = async (orderId) => {
    await resumeOrder(orderId);
    handleRefresh();
    if (!viewingDomain?.id) return;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const updated = await getDomainById(viewingDomain.id);
      if (updated) setViewingDomain(updated);
    }
  };

  const paginationConfig = {
    ...pagination,
    total: totalElements,
    showSizeChanger: true,
    showTotal: (total) => `Total ${total} domains`,
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="globe"
          title="Domains"
          subtitle="Manage all registered domains, DNS, and hosting configuration"
          className="mb-3 mt-3 flex-1"
        />

        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Space>
      </div>

      <DomainsTable
        domains={domains}
        loading={loading}
        filters={filters}
        setFilters={handleFiltersChange}
        pagination={paginationConfig}
        onPageChange={handlePageChange}
        onView={handleView}
      />

      <DomainViewDrawer
        domain={viewingDomain}
        open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        onResume={handleResume}
      />
    </>
  );
}